import { Injectable } from '@angular/core';
import { Notification } from '../model/notification';
import { ThemePalette } from '@angular/material/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import * as dayjs from 'dayjs';
import { NotificationSnackbarComponent } from '../component/notification-snackbar/notification-snackbar.component';
import { ConfirmationDialogComponent } from '../component/confirmation-dialog/confirmation-dialog.component';
import { Observable } from 'rxjs';

const MAINTENANCE_WARNING_SESSION_KEY = 'maintenance-warning-closed';

export interface NotificationOptions {
  link?: string;
  read?: boolean;
  params?: {
    [key: string]: any
  };
  overridePanelClass?: string;
  snackbarConfig?: MatSnackBarConfig;

  onOpen?: () => void;
  onClose?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  constructor(
    private snackbar: MatSnackBar,
    private dialog: MatDialog
  ) { }

  notify(message: string, options: NotificationOptions = {}): void {
    const notification = new Notification(
      message, options.link, dayjs(), options.read, options.params,
      options.overridePanelClass, options.snackbarConfig,
      options.onOpen, options.onClose
    );

    this.openSnackbar(notification, notification.snackbarConfig);
  }

  notifyError(message: string, options: NotificationOptions = {}): void {
    options.overridePanelClass = 'error';
    this.notify(message, options);
  }

  notifyWarning(message: string, options: NotificationOptions = {}): void {
    options.overridePanelClass = 'warning';
    options.snackbarConfig = {
      panelClass: 'warning',
      horizontalPosition: 'right',
      verticalPosition: 'top',
      duration: 0,
    };
    this.notify(message, options);
  }

  notifyScheduledMaintenance(): void {
    if (sessionStorage.getItem(MAINTENANCE_WARNING_SESSION_KEY)) {
      return;
    }

    this.notifyWarning('messages.scheduled-maintenance', {
      onClose: () => {
        sessionStorage.setItem(MAINTENANCE_WARNING_SESSION_KEY, 'true');
      }
    });
  }

  clearScheduledMaintenanceWarning(): void {
    sessionStorage.removeItem(MAINTENANCE_WARNING_SESSION_KEY);
  }

  notifyWith(notification: Notification): void {
    this.openSnackbar(notification, notification.snackbarConfig);
  }

  openSnackbar(notification: Notification, config: MatSnackBarConfig = {
    panelClass: 'notification',
    horizontalPosition: 'left',
    verticalPosition: 'bottom',
    duration: 5000,
  }): void {
    if (notification.overridePanelClass?.length > 0) {
      config.panelClass = notification.overridePanelClass;
    }

    const ref = this.snackbar.openFromComponent(NotificationSnackbarComponent, { data: notification, ...config });
    notification.callback(notification.onOpen);

    ref.afterDismissed().subscribe(() => {
      notification.callback(notification.onClose);
    });
  }

  openConfirmationDialog(data): Observable<boolean> {
    if (data) {
      return this.dialog.open(ConfirmationDialogComponent, {data}).afterClosed();
    } else {
      return this.dialog.open(ConfirmationDialogComponent).afterClosed();
    }
  }
}
